import { TableHeader, TableRow, TableHead } from "../ui/table";
import { Checkbox } from "../ui/checkbox";
import type { TableColumn, ValidTableNames } from "@/hooks/table/types";
import { ReactNode, useState } from "react";
import { useCommonI18n } from "@/hooks/useModuleI18n";
import { useColumnWidths } from "@/hooks/useColumnWidths";
import { ColumnResizeHandle } from "./column-resize-handle";
import {
  ArrowUpDown,
  GripVertical,
  Pin,
  PinOff,
  ArrowLeftToLine,
  ArrowRightToLine,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { CSSProperties } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import type { PinSide } from "@/hooks/useColumnPin";

/**
 * Anchos por defecto por key de columna cuando el usuario no ha persistido
 * ninguno. Evita que una columna quede demasiado angosta tras una sesión
 * previa de resize (ej: "Últ. Ve..." en lugar de "Última Venta").
 */
const COLUMN_DEFAULT_WIDTHS: Record<string, number> = {
  lastUpdatedBy: 220,
};

interface DataTableHeaderProps<T extends ValidTableNames = any> {
  displayColumns: TableColumn<T>[];
  enableRowSelection?: boolean;
  allRowsSelected: boolean;
  handleSelectAll: () => void;
  handleColumnHeaderClick: (columnKey: string, e: React.MouseEvent) => void;
  getSortIcon: (columnKey: string) => ReactNode;
  getSortDirection: (columnKey: string) => "asc" | "desc" | null;
  hasActions: boolean;
  tableId?: string;
  /**
   * Reordena columnas por drag & drop. `position` indica si la columna
   * arrastrada cae antes ("before") o después ("after") de `toKey`,
   * determinada por el lado del puntero sobre la columna destino.
   */
  onReorderColumn?: (
    fromKey: string,
    toKey: string,
    position: "before" | "after",
  ) => void;
  /** Pin de columnas (izquierda/derecha). */
  enablePin?: boolean;
  getPinSide?: (colKey: string) => PinSide | undefined;
  onSetPin?: (colKey: string, side: PinSide | null) => void;
  /** Estilos sticky por key para las columnas pinneadas. */
  pinnedStyles?: Record<string, CSSProperties>;
  /** Fija la columna de selección (checkbox) a la izquierda (hay pins). */
  selectionSticky?: boolean;
}

export default function DataTableHeader<T extends ValidTableNames = any>({
  displayColumns,
  enableRowSelection,
  allRowsSelected,
  handleSelectAll,
  handleColumnHeaderClick,
  getSortIcon,
  getSortDirection,
  hasActions,
  tableId,
  onReorderColumn,
  enablePin,
  getPinSide,
  onSetPin,
  pinnedStyles,
  selectionSticky,
}: DataTableHeaderProps<T>) {
  const { t } = useCommonI18n();
  const { getWidth, setWidth, resetWidth } = useColumnWidths(tableId);
  // Slot de drop: hay N+1 slots para N columnas. Slot i es el borde entre
  // la columna i-1 y la columna i (slot 0 = antes de la primera, slot N =
  // después de la última). El indicador se renderiza como una sola barra en
  // el borde del slot, evitando dobles bordes entre columnas vecinas.
  const [dropSlot, setDropSlot] = useState<number | null>(null);
  // Hysteresis: zona muerta en px alrededor del midpoint para evitar que el
  // indicador parpadee cuando el cursor cruza justo la frontera.
  const SIDE_DEAD_ZONE_PX = 6;
  const canReorder = !!onReorderColumn;
  const canPin = !!enablePin && !!onSetPin;
  // Padding derecho reservado para el cluster de controles (pin/grip).
  const controlsCount = (canReorder ? 1 : 0) + (canPin ? 1 : 0);
  const controlsPad =
    controlsCount >= 2 ? "pr-9" : controlsCount === 1 ? "pr-6" : undefined;

  return (
    <TableHeader className="rounded-t-lg sticky top-0 z-20 bg-card">
      <TableRow >
        {enableRowSelection && (
          <TableHead
            className={cn(
              "w-9! h-8! p-0!",
              selectionSticky &&
                "sticky left-0 z-30 bg-card",
            )}
          >
            <div className="flex items-center justify-start h-full w-full">
              <Checkbox
                checked={allRowsSelected}
                onCheckedChange={handleSelectAll}
                aria-label={t("table.selection.selectAllRows")}
              />
            </div>
          </TableHead>
        )}
        {displayColumns.map((column, idx) => {
          const width = getWidth(column.key);
          const fallbackWidth =
            column.defaultWidth ?? COLUMN_DEFAULT_WIDTHS[column.key];
          const style: React.CSSProperties | undefined =
            typeof width === "number"
              ? { width: `${width}px`, minWidth: `${width}px` }
              : typeof fallbackWidth === "number"
                ? { minWidth: `${fallbackWidth}px` }
                : undefined;
          const isSorted = getSortDirection(column.key as string) !== null;
          const pinSide = getPinSide?.(column.key as string);
          const pinned = !!pinSide;
          const pinStyle = pinnedStyles?.[column.key as string];
          const finalStyle = pinStyle ? { ...style, ...pinStyle } : style;
          // El slot i lo pinta UNA sola th. Regla unificada:
// - Slot 0 (antes de la 1ra col) -> 1ra th pinta su lado izquierdo.
// - Slot i (i>=1) -> la th cuyo borde derecho da al slot, es decir, la th
//   en idx=i-1, pinta su lado derecho. Eso se reduce a dropSlot === idx+1,
//   que vale para cualquier th (incluida la primera y la última).
//   Ej: tabla de 3 cols (idx 0,1,2). dropSlot=1 -> th_0 pinta right-0.
//       dropSlot=2 -> th_1 pinta right-0. dropSlot=3 -> th_2 pinta right-0.
// Como solo hay un slot activo a la vez, solo UNA th pinta UNA barra.
          const isFirstCol = idx === 0;
          const showLeftEdge = isFirstCol && dropSlot === 0;
          const showRightEdge = dropSlot === idx + 1;
          return (
            <TableHead
              key={column.key as string}
              data-column-key={column.key as string}
              className={cn(
                "text-muted-foreground text-sm font-normal relative group/sort",
                column.className,
                // Con reorder activado el header NO ordena al click (se ordena
                // desde el icono ▲▼); sin reorder, mantiene el sort al click.
                column.sortable &&
                  !canReorder &&
                  "cursor-pointer hover:text-foreground transition-colors",
                showLeftEdge &&
                  "before:absolute before:top-0 before:left-0 before:h-full before:w-1 before:bg-primary/60 before:z-0 before:pointer-events-none",
                showRightEdge &&
                  "before:absolute before:top-0 before:right-0 before:h-full before:w-1 before:bg-primary/60 before:z-0 before:pointer-events-none",
                // Columna pinneada: fondo sólido (sticky) + z sobre el header padre
                // para que conviva con el corner checkbox (z-30) sin parpadeos.
                pinned &&
                  "z-30 bg-card",
              )}
              style={finalStyle}
              onClick={
                canReorder
                  ? undefined
                  : (e) => {
                      const target = e.currentTarget;
                      if (target.getAttribute("data-resizing") === "true") {
                        return;
                      }
                      column.sortable &&
                        handleColumnHeaderClick(column.key as string, e);
                    }
              }
              onDragOver={
                canReorder
                  ? (e) => {
                      // Solo arrastres de columnas (no archivos, textos ni
                      // campos del editor de colecciones).
                      if (!e.dataTransfer.types.includes("column-key")) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      // Midpoint de la columna en cada dragover (barato). Un
                      // cache por key quedaba desfasado si la tabla se
                      // desplazaba horizontalmente durante el arrastre o si
                      // cambiaba el ancho, y la columna caia del lado
                      // equivocado. El re-render solo ocurre al cambiar de slot.
                      const rect = e.currentTarget.getBoundingClientRect();
                      const mid = rect.left + rect.width / 2;
                      const offset = e.clientX - mid;
                      // Mapear a un slot entre columnas. Slot i = borde izq de
                      // la col i. Mitad izquierda -> slot idx, mitad derecha
                      // -> slot idx+1. slotIdx se mantiene estable a través
                      // de cambios de columna, lo que evita parpadeo en la
                      // frontera.
                      let slotIdx: number;
                      if (Math.abs(offset) < SIDE_DEAD_ZONE_PX) {
                        // Zona muerta: conservar el slot actual si el slot
                        // vigente cae dentro de esta columna. Evita flip-flop
                        // justo sobre el midpoint.
                        const inThisCol =
                          dropSlot === idx || dropSlot === idx + 1;
                        slotIdx = inThisCol ? (dropSlot as number) : idx;
                      } else {
                        slotIdx = offset < 0 ? idx : idx + 1;
                      }
                      if (dropSlot !== slotIdx) {
                        setDropSlot(slotIdx);
                      }
                    }
                  : undefined
              }
              onDragLeave={
                canReorder
                  ? () => {
                      // No-op intencional: limpiar aquí causa parpadeo porque
                      // onDragLeave se dispara al cruzar hijos (label, sort,
                      // controles). El indicador se limpia en onDrop / onDragEnd.
                    }
                  : undefined
              }
              onDrop={
                canReorder
                  ? (e) => {
                      e.preventDefault();
                      const fromKey = e.dataTransfer.getData("column-key");
                      const toKey = column.key as string;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const midpoint = rect.left + rect.width / 2;
                      const side: "before" | "after" =
                        dropSlot === idx || dropSlot === idx + 1
                          ? dropSlot === idx
                            ? "before"
                            : "after"
                          : e.clientX < midpoint
                            ? "before"
                            : "after";
                      setDropSlot(null);
                      if (fromKey && fromKey !== toKey) {
                        onReorderColumn?.(fromKey, toKey, side);
                      }
                    }
                  : undefined
              }
            >
              <div
                className={cn(
                  "flex items-center gap-2 select-none",
                  column.className?.includes("justify-end") && "justify-end",
                  // Reservar espacio a la derecha para el cluster de controles
                  // (pin/grip, absoluto), así el sort no se superpone con ellos.
                  controlsPad,
                )}
              >
                <span>{column.label}</span>
                {column.sortable && (
                  <span
                    role={canReorder ? "button" : undefined}
                    aria-label={canReorder ? "Ordenar" : undefined}
                    onClick={
                      canReorder
                        ? (e) => {
                            e.stopPropagation();
                            handleColumnHeaderClick(column.key as string, e);
                          }
                        : undefined
                    }
                    className={cn(
                      isSorted
                        ? "opacity-100"
                        : "opacity-0 group-hover/sort:opacity-100 transition-opacity",
                      // Con reorder, el icono es el disparador del sort.
                      canReorder && "cursor-pointer hover:text-foreground",
                    )}
                  >
                    {isSorted ? (
                      getSortIcon(column.key as string)
                    ) : (
                      <ArrowUpDown className="w-4 h-4 opacity-50" />
                    )}
                  </span>
                )}
              </div>
              {(canReorder || canPin) && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1">
                  {canPin && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <span
                          role="button"
                          aria-label="Fijar columna"
                          title="Fijar columna"
                          onClick={(e) => e.stopPropagation()}
                          className={cn(
                            "cursor-pointer transition-opacity",
                            pinned
                              ? "opacity-100 text-primary"
                              : "opacity-0 group-hover/sort:opacity-100 text-muted-foreground/50 hover:text-foreground",
                          )}
                        >
                          <Pin className="w-3.5 h-3.5" />
                        </span>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[10rem]">
                        <DropdownMenuItem
                          className="text-xs gap-2"
                          disabled={pinSide === "left"}
                          onClick={() => onSetPin?.(column.key as string, "left")}
                        >
                          <ArrowLeftToLine className="w-3.5 h-3.5" />
                          Fijar a la izquierda
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-xs gap-2"
                          disabled={pinSide === "right"}
                          onClick={() =>
                            onSetPin?.(column.key as string, "right")
                          }
                        >
                          <ArrowRightToLine className="w-3.5 h-3.5" />
                          Fijar a la derecha
                        </DropdownMenuItem>
                        {pinned && (
                          <DropdownMenuItem
                            className="text-xs gap-2"
                            onClick={() => onSetPin?.(column.key as string, null)}
                          >
                            <PinOff className="w-3.5 h-3.5" />
                            Desfijar
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {canReorder && (
                    <span
                      role="button"
                      aria-label="Reordenar columna"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData(
                          "column-key",
                          column.key as string,
                        );
                      }}
                      onDragEnd={() => setDropSlot(null)}
                      onClick={(e) => e.stopPropagation()}
                      className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-foreground opacity-0 group-hover/sort:opacity-100 transition-opacity"
                    >
                      <GripVertical className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              )}
              <ColumnResizeHandle
                columnKey={column.key as string}
                currentWidth={width}
                defaultWidth={column.defaultWidth}
                minWidth={column.minWidth}
                onCommit={setWidth}
                onReset={resetWidth}
              />
            </TableHead>
          );
        })}
        {hasActions && (
          <TableHead className="w-8! min-w-8! max-w-8! text-center font-regular sticky right-0 z-30 bg-card">
            <span className="sr-only">Acciones</span>
          </TableHead>
        )}
      </TableRow>
    </TableHeader>
  );
}
